/**
 * Drag-to-reorder orchestration for the v8 {@link DataTable}.
 *
 * The commit target is the table instance's own `columnOrder` + `table.setColumnOrder`. The hook
 * owns the dnd-kit sensors and the `onDragEnd` that turns a header drop into a new column order.
 *
 * The drop only reshuffles the DRAGGABLE subset (visible columns that are NOT sticky, fixed-px
 * or lock-visible) via `arrayMove`; the moved sequence is then poured back into the draggable
 * slots of the FULL order snapshot, leaving sticky / fixed / hidden columns in their absolute
 * positions — so the sticky prefix is preserved by construction and a re-shown column reappears
 * at its remembered slot. The result is pushed through a SINGLE `setColumnOrder` call:
 *   - When `columnOrder` is controlled by the owner (the Invoices opt-in path), v8 holds no
 *     internal order state, so `table.setColumnOrder(next)` routes straight to the owner's
 *     `onColumnOrderChange` (DataTable bridges the v8 updater to the resolved array), which
 *     persists via `useColumnLayoutPersistence.saveOrder`. Calling the owner callback separately
 *     here too would DOUBLE-fire it.
 *   - When reorder is on but the order is uncontrolled (no persistence owner), `table.setColumnOrder`
 *     updates the instance's own `columnOrder` state, so in-session reorder still works.
 * The commit is synchronous so dnd-kit's drop animation can read the header's settled DOM
 * position; the REUSED `reconcileColumnOrder` re-validates the persisted order at read time.
 *
 * `onDragEnd` is referentially stable (it is read by `DataTable`, which keeps it out of its
 * hand-memoized body): its live inputs are reached through refs rather than recreating the
 * callback every render.
 */
import { closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

const EMPTY_IDS: string[] = [];

type UseDataTableReorderProps = {
    /** Persisted layout, non-mobile — reordering is active. */
    enabled: boolean;
    /** Visible column ids in their current render order. */
    visibleColumnIds: string[];
    /** Ids that must not move: the sticky prefix UNION fixed-width UNION lock-visible columns —
     *  they keep their slots so the sticky prefix can't be broken. */
    nonDraggableIds: ReadonlySet<string>;
    /** Full current order over ALL column ids (incl. hidden) — the base a drop is computed
     *  against, so hidden columns keep their slots. */
    currentOrder: string[];
    /** v8 instance setter (`table.setColumnOrder`): the SINGLE commit channel. When the order is
     *  controlled it routes through the owner's `onColumnOrderChange` (→ saveOrder); when
     *  uncontrolled it updates the instance's own state. Either way it commits synchronously so
     *  dnd-kit's drop animation reads the settled position. */
    setColumnOrder: (order: string[]) => void;
};

export const useDataTableReorder = ({
    enabled,
    visibleColumnIds,
    nonDraggableIds,
    currentOrder,
    setColumnOrder,
}: UseDataTableReorderProps) => {
    const sensors = useSensors(
        // distance:5 so a plain click still reaches the header sort toggle and the right-edge
        // resize handle — a drag only begins once the pointer travels past the threshold.
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const sortableIds = useMemo(
        () => (enabled ? visibleColumnIds.filter((id) => !nonDraggableIds.has(id)) : EMPTY_IDS),
        [enabled, visibleColumnIds, nonDraggableIds]
    );

    const sortableIdsRef = useRef(sortableIds);
    const currentOrderRef = useRef(currentOrder);
    const setColumnOrderRef = useRef(setColumnOrder);
    useLayoutEffect(() => {
        sortableIdsRef.current = sortableIds;
        currentOrderRef.current = currentOrder;
        setColumnOrderRef.current = setColumnOrder;
    });

    // A grabbing cursor for the whole document while a column is held, so the interaction reads
    // as "carrying" the column even when the pointer leaves the header.
    const onDragStart = useCallback(() => {
        document.body.style.cursor = 'grabbing';
    }, []);
    const onDragCancel = useCallback(() => {
        document.body.style.cursor = '';
    }, []);

    // If the hook unmounts mid-drag (route change, table swap), neither onDragEnd nor onDragCancel
    // is guaranteed to run — reset the global cursor on unmount so it can never stay stuck on
    // 'grabbing'.
    useLayoutEffect(
        () => () => {
            document.body.style.cursor = '';
        },
        []
    );

    const onDragEnd = useCallback((event: DragEndEvent) => {
        document.body.style.cursor = ''; // restore before any early return
        const { active, over } = event;
        if (!over) return;
        const activeId = String(active.id);
        const overId = String(over.id);
        if (activeId === overId) return;

        const ids = sortableIdsRef.current;
        const oldIndex = ids.indexOf(activeId);
        const newIndex = ids.indexOf(overId);
        if (oldIndex === -1 || newIndex === -1) return;

        const movedDraggable = arrayMove(ids, oldIndex, newIndex);
        const draggable = new Set(ids);

        // Refill each draggable slot of the full snapshot with the moved sequence in turn; sticky
        // / fixed / hidden ids keep their absolute positions.
        let cursor = 0;
        const newOrder = currentOrderRef.current.map((id) => (draggable.has(id) ? movedDraggable[cursor++] : id));
        // Safety net: append any moved ids the snapshot didn't account for (it should account for
        // all of them) so none are dropped.
        for (; cursor < movedDraggable.length; cursor++) newOrder.push(movedDraggable[cursor]);

        // Single commit channel: when controlled this routes to the owner's onColumnOrderChange
        // (→ saveOrder); when uncontrolled it updates the v8 instance state. No separate owner call
        // here — that would double-fire persistence under controlled state.
        setColumnOrderRef.current(newOrder);
    }, []);

    return {
        sensors,
        sortableIds,
        onDragStart,
        onDragEnd,
        onDragCancel,
        collisionDetection: closestCenter,
        reorderEnabled: enabled,
    };
};
